// Smart Sash Admin Panel - RFID Authentication Context
// Manages RFID authentication and gRPC event streaming

import {
  postApiRfidAssignmentAuthenticate,
  type AssignmentAuthRequest,
} from '@smartsash/api-client';
import React, { createContext, type ReactNode, useContext, useState } from 'react';

import { authClient } from './authClient';
import { tokenManager } from './tokenManager';

import { HTTP_MOVED_PERMANENTLY, HTTP_OK } from '@/utils/httpStatus';

interface RFIDAuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  sessionId: string | null;
  authenticate: (sessionId: string, login: string, password: string) => Promise<boolean>;
  logout: () => void;
}

const RFIDAuthContext = createContext<RFIDAuthContextType | undefined>(undefined);

interface RFIDAuthProviderProps {
  children: ReactNode;
}

/**
 * RFID Authentication Provider Component
 */
export const RFIDAuthProvider: React.FC<RFIDAuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);

  /**
   * Authenticate user for RFID registration
   */
  const authenticate = async (
    sessionId: string,
    login: string,
    password: string,
  ): Promise<boolean> => {
    setIsLoading(true);
    setError(null);

    try {
      // HS-842: the payload shape is the generated `AssignmentAuthRequest`, not a hand-written
      // local copy. Its fields are all optional (`institutionId` too — this public flow does not
      // send one), so the object literal is a subset by design.
      const request: AssignmentAuthRequest = {
        sessionId,
        login,
        password,
      };

      // Authenticate via API. Like the regular login, auth tokens ride as HttpOnly cookies
      // (invisible to JS) — the response body carries no accessToken/refreshToken. A 200 means
      // the cookies are set; a 301 means a password change is required. Sync via /Auth/me.
      //
      // HS-847 P5: 301 must count as success or axios rejects before the flag can be read, and
      // `skipAuthRefresh` keeps a 401 on this public route out of the refresh → sessionExpired path.
      const body = await postApiRfidAssignmentAuthenticate(request, {
        skipAuthRefresh: true,
        validateStatus: (status: number) => status === HTTP_OK || status === HTTP_MOVED_PERMANENTLY,
      });
      const requiresPasswordChange =
        typeof body === 'object' &&
        body !== null &&
        (body as { requiresPasswordChange?: unknown }).requiresPasswordChange === true;

      tokenManager.setPasswordChangeRequired(requiresPasswordChange);
      // HS-859: was `syncAuthState()` on the deleted facade, which forwarded to exactly this
      // call. Note `restore()` is deduplicated behind the client's `ready` flag, which the root
      // guard has already set by the time this route renders — so this is a no-op today. That
      // was equally true of `syncAuthState`, and the flow does not depend on it: the onboarding
      // screens drive off this provider's own state and `tokenManager`, not the global session.
      await authClient.restore();

      if (requiresPasswordChange) {
        setSessionId(null);
        setIsAuthenticated(false);
        return true;
      }

      setSessionId(sessionId);
      setIsAuthenticated(true);
      return false;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Authentication failed';
      setError(errorMessage);
      console.error('RFID authentication error:', err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Logout and cleanup
   */
  const logout = (): void => {
    setIsLoading(true);
    try {
      // HS-847 P5: `setAuthToken(null)` is gone with the local axios instance. Admin
      // authenticates by HttpOnly cookie, so there was never an in-memory header to clear.
      tokenManager.clearTokens();
      setIsAuthenticated(false);
      setSessionId(null);
      setError(null);
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const value: RFIDAuthContextType = {
    isAuthenticated,
    isLoading,
    error,
    sessionId,
    authenticate,
    logout,
  };

  return <RFIDAuthContext.Provider value={value}>{children}</RFIDAuthContext.Provider>;
};

/**
 * Custom hook to use RFID auth context
 */
export const useRFIDAuth = (): RFIDAuthContextType => {
  const context = useContext(RFIDAuthContext);
  if (context === undefined) {
    throw new Error('useRFIDAuth must be used within an RFIDAuthProvider');
  }
  return context;
};
